import React, { useState } from 'react';
import { Save, Shield, Globe, Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';

const AdminSettingsPage = () => {
    const [isSaving, setIsSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Settings state
    const [brandName, setBrandName] = useState('cointocash');
    const [adminEmail, setAdminEmail] = useState('admin@cointocash.pro');
    const [maintenanceMode, setMaintenanceMode] = useState(false);

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordStatus, setPasswordStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error', message: string }>({ type: 'idle', message: '' });

    const handleSaveGeneral = async () => {
        setIsSaving(true);
        // Simulate API call - In a real app, save to Firestore 'settings/global'
        setTimeout(() => {
            setIsSaving(false);
            alert('설정이 저장되었습니다.');
        }, 1000);
    };

    const handleUpdatePassword = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordStatus({ type: 'error', message: '모든 필드를 입력해 주세요.' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordStatus({ type: 'error', message: '새 비밀번호가 일치하지 않습니다.' });
            return;
        }

        setPasswordStatus({ type: 'loading', message: '처리 중...' });

        try {
            const user = auth.currentUser;
            if (!user || !user.email) throw new Error("사용자 정보를 찾을 수 없습니다.");

            // Firebase updatePassword often requires recent login, so we re-authenticate
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            await updatePassword(user, newPassword);

            setPasswordStatus({ type: 'success', message: '비밀번호가 성공적으로 변경되었습니다.' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            console.error(error);
            setPasswordStatus({ type: 'error', message: `오류 발생: ${error.message === 'auth/wrong-password' ? '현재 비밀번호가 틀렸습니다.' : error.message}` });
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-3xl font-black tracking-tight text-white uppercase italic">시스템 설정</h2>
                    <p className="text-slate-400 text-sm font-medium mt-1 uppercase tracking-widest">플랫폼 전역 설정 및 보안 관리</p>
                </div>
                <button
                    onClick={handleSaveGeneral}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-8 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-emerald-600/20 active:scale-95 disabled:opacity-50"
                >
                    <Save className="h-5 w-5" />
                    {isSaving ? '저장 중...' : '설정 저장'}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-left">
                {/* General Settings */}
                <div className="bg-slate-950/50 border border-slate-800 rounded-[2.5rem] p-10 space-y-8 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/[0.03] blur-3xl rounded-full"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="p-3 rounded-2xl bg-blue-500/10 text-blue-500 group-hover:scale-110 transition-transform">
                            <Globe className="h-6 w-6" />
                        </div>
                        <h3 className="text-xl font-black text-white uppercase italic">기본 사이트 정보</h3>
                    </div>

                    <div className="space-y-6 relative z-10">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">사이트 브랜드명</label>
                            <input
                                type="text"
                                value={brandName}
                                onChange={(e) => setBrandName(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-white font-bold focus:outline-none focus:border-blue-500/50 transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">관리자 지원 이메일</label>
                            <input
                                type="email"
                                value={adminEmail}
                                onChange={(e) => setAdminEmail(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-white font-bold focus:outline-none focus:border-blue-500/50 transition-all"
                            />
                        </div>
                        <div
                            className="flex items-center justify-between p-4 bg-slate-900/50 rounded-2xl border border-slate-800 cursor-pointer"
                            onClick={() => setMaintenanceMode(!maintenanceMode)}
                        >
                            <div>
                                <p className="text-sm font-black text-white uppercase">점검 모드 (Maintenance)</p>
                                <p className="text-xs text-slate-500 font-bold uppercase mt-1 italic tracking-tighter">업데이트를 위한 사용자 접속 제한</p>
                            </div>
                            <div className={cn(
                                "w-12 h-6 rounded-full relative transition-colors duration-300",
                                maintenanceMode ? "bg-red-500/20" : "bg-slate-800"
                            )}>
                                <div className={cn(
                                    "absolute top-1 w-4 h-4 rounded-full transition-all duration-300",
                                    maintenanceMode ? "right-1 bg-red-500" : "left-1 bg-slate-500"
                                )}></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Password Change Settings (New) */}
                <div className="bg-slate-950/50 border border-slate-800 rounded-[2.5rem] p-10 space-y-8 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/[0.03] blur-3xl rounded-full"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="p-3 rounded-2xl bg-purple-500/10 text-purple-500 group-hover:scale-110 transition-transform">
                            <Lock className="h-6 w-6" />
                        </div>
                        <h3 className="text-xl font-black text-white uppercase italic">보안 자격 증명</h3>
                    </div>

                    <div className="space-y-6 relative z-10">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between px-1">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">현재 비밀번호</label>
                                <button
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="text-[10px] font-black text-slate-500 hover:text-white uppercase transition-colors"
                                >
                                    {showPassword ? <EyeOff className="h-3 w-3 inline mr-1" /> : <Eye className="h-3 w-3 inline mr-1" />}
                                    {showPassword ? '숨기기' : '보기'}
                                </button>
                            </div>
                            <input
                                type={showPassword ? "text" : "password"}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-white font-bold focus:outline-none focus:border-purple-500/50 transition-all placeholder:text-slate-700"
                                placeholder="현재 비밀번호 입력"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">새 비밀번호</label>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-white font-bold focus:outline-none focus:border-purple-500/50 transition-all placeholder:text-slate-700"
                                    placeholder="새 비밀번호"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">새 비밀번호 확인</label>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-white font-bold focus:outline-none focus:border-purple-500/50 transition-all placeholder:text-slate-700"
                                    placeholder="비밀번호 확인"
                                />
                            </div>
                        </div>

                        {passwordStatus.type !== 'idle' && (
                            <div className={cn(
                                "flex items-center gap-2 p-3 rounded-xl text-[10px] font-black uppercase tracking-tighter",
                                passwordStatus.type === 'success' ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" :
                                    passwordStatus.type === 'error' ? "bg-red-500/10 text-red-500 border border-red-500/20" :
                                        "bg-slate-800 text-slate-400"
                            )}>
                                {passwordStatus.type === 'success' ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                                {passwordStatus.message}
                            </div>
                        )}

                        <button
                            onClick={handleUpdatePassword}
                            disabled={passwordStatus.type === 'loading'}
                            className="w-full h-12 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-purple-600/20 active:scale-95 disabled:opacity-50"
                        >
                            {passwordStatus.type === 'loading' ? '처리 중...' : '비밀번호 변경 적용'}
                        </button>
                    </div>
                </div>

                {/* Legacy Security Policy (Read Only for now) */}
                <div className="bg-slate-950/50 border border-slate-800 rounded-[2.5rem] p-10 space-y-8 opacity-50">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-500">
                            <Shield className="h-6 w-6" />
                        </div>
                        <h3 className="text-xl font-black text-white uppercase italic">플랫폼 보안 및 정책</h3>
                    </div>

                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">출금 승인 방식</label>
                            <select disabled className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-white font-bold">
                                <option>수동 검토 필수</option>
                            </select>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-2xl border border-slate-800">
                            <div>
                                <p className="text-sm font-black text-white uppercase">고급 2FA 보호</p>
                                <p className="text-xs text-slate-500 font-bold uppercase mt-1 italic tracking-tighter">티어 3 보안 표준 활성화</p>
                            </div>
                            <div className="w-12 h-6 bg-amber-600/20 rounded-full relative">
                                <div className="absolute right-1 top-1 w-4 h-4 bg-amber-500 rounded-full"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Utility for cleaner class joining
function cn(...classes: any[]) {
    return classes.filter(Boolean).join(' ');
}

export default AdminSettingsPage;
